import { api } from './index';

export enum MeterReadingType {
  ELECTRICITY = 'ELECTRICITY',
  WATER = 'WATER',
}

export interface MeterReading {
  id: string;
  apartmentId: string;
  buildingId: string;
  type: MeterReadingType;
  oldReading: number | null;
  newReading: number;
  period: string;
  imagePath?: string;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  apartment: {
    id: string;
    code: string;
    ownerName: string;
  };
  building: {
    id: string;
    name: string;
  };
  createdBy: {
    id: string;
    name: string;
  };
}

export interface CreateMeterReadingData {
  apartmentId: string;
  buildingId: string;
  type: MeterReadingType;
  oldReading: string;
  newReading: string;
  period: string;
  imagePath?: string;
}

export interface UpdateMeterReadingData {
  oldReading: string;
  newReading: string;
  imagePath?: string;
}

export interface GetMeterReadingsQuery {
  buildingId?: string;
  type?: MeterReadingType;
  period?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export type MeterReadingRegistryAction = 'CREATE' | 'UPDATE' | 'DELETE';

export interface MeterReadingActionLog {
  id: string;
  buildingId: string;
  meterReadingId?: string | null;
  apartmentId: string;
  utilityMeterId?: string | null;
  type: MeterReadingType;
  period: string;
  action: MeterReadingRegistryAction;
  oldReading?: number | null;
  newReading?: number | null;
  imagePath?: string | null;
  notes?: string | null;
  metadata?: {
    before?: {
      oldReading?: number | null;
      newReading?: number | null;
      imagePath?: string | null;
    };
  } | null;
  createdBy?: string | null;
  createdAt: string;
  apartment: {
    id: string;
    code: string;
    ownerName: string;
  };
  creator?: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export interface ApartmentMeterData {
  id: string;
  utilityMeterId?: string;
  apartmentCode: string;
  apartmentName: string;
  meterCode?: string;
  billingMode?: string;
  feeMultiplier?: number | null;
  quotaTierStart?: string;
  quotaTierLevel?: number;
  oldReading: number | null;
  newReading?: number;
  usage?: number;
  previousPeriodUsage?: number;
  imagePath?: string;
  hasExistingReading: boolean;
  existingReadingId?: string;
}

export interface PaginatedMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginatedMeta;
}

function mapMeterToApartmentData(meter: Record<string, unknown>): ApartmentMeterData {
  return {
    id: meter.apartmentId as string,
    utilityMeterId: meter.id as string,
    apartmentCode: meter.apartmentCode as string,
    apartmentName: meter.apartmentName as string,
    meterCode: meter.meterCode as string | undefined,
    billingMode: meter.billingMode as string | undefined,
    feeMultiplier: meter.feeMultiplier as number | null | undefined,
    quotaTierStart: meter.quotaTierStart as string | undefined,
    quotaTierLevel: meter.quotaTierLevel as number | undefined,
    oldReading: (meter.oldReading as number | null) ?? 0,
    newReading: meter.newReading as number | undefined,
    usage: meter.usage as number | undefined,
    previousPeriodUsage: meter.previousPeriodUsage as number | undefined,
    imagePath: meter.imagePath as string | undefined,
    hasExistingReading: (meter.hasExistingReading as boolean) ?? false,
    existingReadingId: meter.existingReadingId as string | undefined,
  };
}

export const meterReadingsApi = {
  // Create new meter reading
  async create(data: CreateMeterReadingData): Promise<MeterReading> {
    const response = await api.post('/meter-readings', data);
    return response.data;
  },

  // Get previous period reading for an apartment and type
  async getPreviousReading(
    apartmentId: string,
    type: MeterReadingType,
    period: string,
    utilityMeterId?: string,
  ): Promise<number> {
    const encoded = encodeURIComponent(period);
    const params = utilityMeterId
      ? `?utilityMeterId=${encodeURIComponent(utilityMeterId)}`
      : '';
    const response = await api.get(
      `/meter-readings/previous/${apartmentId}/${type}/${encoded}${params}`,
    );
    return (response.data && typeof response.data.previousReading === 'number')
      ? response.data.previousReading
      : 0;
  },

  // Get meter reading by ID
  async getById(id: string): Promise<MeterReading> {
    const response = await api.get(`/meter-readings/${id}`);
    return response.data;
  },

  // Update meter reading
  async update(id: string, data: UpdateMeterReadingData): Promise<MeterReading> {
    const response = await api.patch(`/meter-readings/${id}`, data);
    return response.data;
  },

  // Delete meter reading
  async delete(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/meter-readings/${id}`);
    return response.data;
  },

  async deleteReadingsByPeriod(
    buildingId: string,
    type: MeterReadingType,
    period: string,
  ): Promise<{ message: string; readingsDeleted: number; invoicesDeleted: number; period: string }> {
    const response = await api.delete(
      `/meter-readings/period?buildingId=${buildingId}&type=${type}&period=${encodeURIComponent(period)}`,
    );
    return response.data;
  },

  // Get reading history
  async getReadingHistory(query: GetMeterReadingsQuery): Promise<{
    data: MeterReading[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (query.buildingId) params.append('buildingId', query.buildingId);
    if (query.type) params.append('type', query.type);
    if (query.period) params.append('period', query.period);
    if (query.search) params.append('search', query.search);
    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());

    const response = await api.get(`/meter-readings/history?${params.toString()}`);
    return response.data;
  },

  async getActionLogs(query: GetMeterReadingsQuery): Promise<{
    data: MeterReadingActionLog[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (query.buildingId) params.append('buildingId', query.buildingId);
    if (query.type) params.append('type', query.type);
    if (query.period) params.append('period', query.period);
    if (query.search) params.append('search', query.search);
    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());

    const response = await api.get(`/meter-readings/action-logs?${params.toString()}`);
    return response.data;
  },

  // Get last readings for a building and type
  async getLastReadings(buildingId: string, type: MeterReadingType): Promise<MeterReading[]> {
    const response = await api.get(`/meter-readings/last/${buildingId}/${type}`);
    return response.data;
  },

  async getMetersWithMeterData(
    buildingId: string,
    type: MeterReadingType,
    period: string,
    options?: {
      page?: number;
      limit?: number;
      search?: string;
      recordedOnly?: boolean;
    },
  ): Promise<PaginatedResponse<ApartmentMeterData>> {
    const params = new URLSearchParams({
      buildingId,
      type,
      period,
    });
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.search) params.append('search', options.search);
    if (options?.recordedOnly) params.append('recordedOnly', 'true');

    const response = await api.get(`/meter-readings/meters-data?${params.toString()}`);
    const rawData = response.data.data ?? response.data;
    const data = (Array.isArray(rawData) ? rawData : []).map((meter) =>
      mapMeterToApartmentData(meter as Record<string, unknown>),
    );
    return {
      data,
      meta: response.data.meta ?? {
        total: data.length,
        page: 1,
        limit: data.length,
        totalPages: 1,
      },
    };
  },

  // Get apartments with meter data for bulk entry (legacy)
  async getApartmentsWithMeterData(
    buildingId: string, 
    type: MeterReadingType, 
    period: string
  ): Promise<ApartmentMeterData[]> {
    const response = await api.get(`/meter-readings/apartments-data?buildingId=${buildingId}&type=${type}&period=${period}`);
    return response.data;
  },

  // Submit bulk meter readings
  async submitBulkReadings(data: {
    buildingId: string;
    type: MeterReadingType;
    period: string;
    readings: Array<{
      apartmentId?: string;
      utilityMeterId?: string;
      oldReading: string;
      newReading: string;
      imagePath?: string;
    }>;
  }): Promise<{
    message: string;
    createdCount: number;
    updatedCount: number;
    baselineUpdatedCount?: number;
  }> {
    const response = await api.post('/meter-readings/bulk-submit', data, {
      timeout: 300_000,
    });
    return response.data;
  },

  // Import data from Excel file
  async importFromFile(formData: FormData): Promise<ApartmentMeterData[]> {
    const response = await api.post('/meter-readings/import-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300_000,
    });
    return response.data;
  },

  async downloadExport(
    buildingId: string,
    type: MeterReadingType,
    period: string,
    endpoint: 'export' | 'export-sample',
  ): Promise<void> {
    const response = await api.get(
      `/meter-readings/${endpoint}?buildingId=${buildingId}&type=${type}&period=${encodeURIComponent(period)}`,
      { responseType: 'blob' },
    );
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename =
      filenameMatch?.[1] ||
      (type === MeterReadingType.WATER ? 'chi_so_nuoc.xlsx' : 'chi_so_dien.xlsx');
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  async exportReadings(
    buildingId: string,
    type: MeterReadingType,
    period: string,
  ): Promise<void> {
    return this.downloadExport(buildingId, type, period, 'export');
  },

  async exportSampleReadings(
    buildingId: string,
    type: MeterReadingType,
    period: string,
  ): Promise<void> {
    return this.downloadExport(buildingId, type, period, 'export-sample');
  },

  /** @deprecated use exportSampleReadings */
  async exportSampleFile(
    buildingId: string,
    type: MeterReadingType,
    period: string,
  ): Promise<void> {
    return this.exportSampleReadings(buildingId, type, period);
  },
};
